import { Injectable } from '@angular/core';
import { ChartDefinitionBase } from './chart-definition-base';
import { IMachine } from '../api/domain-model';
import { MachineService } from '../api/machine.service';
import { NotificationService } from '../shared/notifications/notification.service';
import { ChartConfigService } from '../company/chart-config.service';
import { AuthService } from '../shared/auth-service';
import { TranslateService } from '../shared/translate';
import { IContainerTableCardDefinition, IGaugeCardDefinition, IInterruptsTableCardDefinition, ISimpleContentCardDefinition, IWeightDataTableCardDefinition } from '../charts/definition-model';
import CardDisplayVariables from '../common/domain/card-display-variables';
import { Identifiers, Util } from '../common/domain/bindings';

@Injectable()
export class ChartDefinitionMiniadamsService extends ChartDefinitionBase {

	private machine: IMachine;

	constructor(
		public machineService: MachineService,
		public notificationService: NotificationService,
		public chartConfigService: ChartConfigService,
		public authService: AuthService,
		public translateService: TranslateService
	) {
		super();
	}

	setMachine(machine: IMachine): void {
		this.machine = machine;
	}

	getMachine(): IMachine {
		return this.machine;
	}

	getTypeChangesCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u16SortChgDay', 'u16SortChgBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A035TypeChange',
			cardBatchTitle: 'A035TypeChangeBatch',
			unit: 'A020Count',
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().typeChangesToday,
		}
	}

	getEmptyRejectsCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u32RejectEmptyCnt', 'u32RejectEmptyCntBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A086EmptyRejects',
			cardBatchTitle: 'A086EmptyRejectsBatch',
			unit: 'A020Count',
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().emptyBagRejectsToday,
		}
	}

	getFullRejectsCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u32RejectFullCnt', 'u32RejectFullCntBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A087FullRejects',
			cardBatchTitle: 'A087FullRejectsBatch',
			unit: 'A020Count',
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().fullBagRejectsToday,
		}
	}

	getDailyProductionCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u16ProdWeightCurr', 'u16ProdWeightCurrBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A094DailyProduction',
			cardBatchTitle: 'A094DailyProductionBatch',
			unit: 'A113Kg',
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().emptyBagRejectsToday,
		}
	}

	getSpeedRelatedProductionCardDefinition(): IGaugeCardDefinition {
		return <IGaugeCardDefinition>{
			chartOptions: this.chartConfigService.anyGaugeChartSpec(
				this.machine,
				'leistungPackerDrehzahlSpec',
				0,
				100,
				Identifiers.miniadamsSpeedRelatedProductionCard,
				null
			).options,
			title: 'A036ProductionRate',
			unit: 'A037SpeedRelated',
			historyVariables: Identifiers.miniadamsSpeedRelatedProductionCard[2],
			colorHistoryVariables: Identifiers.miniadamsSpeedRelatedProductionCard,
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().speedRelatedProduction
		};
	}

	getProductionChartDefinition(): IGaugeCardDefinition {
		return <IGaugeCardDefinition>{
			chartOptions: this.chartConfigService.anyGaugeChartSpec(
				this.machine,
				'produktionGutsaeckeSpec',
				0,
				100,
				Identifiers.miniadamsProductionGoodCard,
				null
			).options,
			title: 'A039GoodWeights',
			unit: 'A027Percent',
			historyVariables: Identifiers.miniadamsProductionGoodCard[2],
			colorHistoryVariables: Identifiers.miniadamsProductionGoodCard,
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().goodProduction
		};
	}

	getInterruptsCardDefinition(): IInterruptsTableCardDefinition {
		const requiredVariables = [
			...Identifiers.miniAdamsInterruptCard.countParams,
			...Identifiers.miniAdamsInterruptCard.timeParams
		];
		return {
			neededValues: requiredVariables,
			historyVariables: ['u16FaultOpReleaseCnt'],
			title: 'A284Interrupts',
			cardBatchTitle: 'A284InterruptsBatch',
			unit: 'A124Minutes',
			machineType: '',
			countParams: Identifiers.miniAdamsInterruptCard.countParams,
			timeParams: Identifiers.miniAdamsInterruptCard.timeParams,
			titleParams: Identifiers.miniAdamsInterruptCard.titleParams,
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().interruptionsToday,
			historyNeededValues: Util.getHistoryNeededValues(Identifiers.miniAdamsInterruptCard.timeParams, Identifiers.miniAdamsInterruptCard.countParams, Identifiers.miniAdamsInterruptCard.titleParams)
		}
	}

	getWeightFillingStationDataCardDefinition(): IWeightDataTableCardDefinition {
		const requiredVariables = Identifiers.fillingStationWeightCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['s32MeanNetScale'],
			netScalePrefix: 's32MeanNetScale',
			netWeightsPrefix: 's32MeanWeights',
			decimals: 's16MeanDecimals',
			title: 'A422WeightData',
			cardBatchTitle: 'A422WeightData',
			unit: 's16MeanUnit',
			multilineChartSpec: this.chartConfigService.miniAdamsFillingStationNetWeightierMultiLineChartConfig(
				Identifiers.fillingStationWeightCard,
				['u8NetWeighersCnt', 'u8SpoutCnt']
			),
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().weightData,
		}
	}

	getStandardDeviationDataCardDefinition(): IWeightDataTableCardDefinition {
		const requiredVariables = Identifiers.fillingStationStandardDeviationCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['s32StdDevNetScale'],
			netScalePrefix: 's32StdDevNetScale',
			netWeightsPrefix: 's32StdDev',
			decimals: 's16StdDevDecimals',
			title: 'A423StdDevData',
			cardBatchTitle: 'A423StdDevData',
			unit: 's16StdDevUnit',
			multilineChartSpec: this.chartConfigService.miniAdamsFillingStationNetWeightierMultiLineChartConfig(
				Identifiers.fillingStationStandardDeviationCard,
				['u8NetWeighersCnt', 'u8SpoutCnt']
			),
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().stdDevData,
		}
	}

	getContainerCardDefinition(): IContainerTableCardDefinition {
		const requiredVariables = Identifiers.containerCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['u32BinProdCnt'],
			title: 'A227Container',
			cardBatchTitle: 'A227Container',
			unit: 'A016Units',
			labelPrefix: 'A227Container',
			recordMaxCount: '10',
			stackedAreaChartSpec: this.chartConfigService.bagCounterStackedAreaChartConfig(
				Identifiers.miniadamsContainerCard,
				'u8BinCnt',
				'A027Percent'
			),
			displayVariable: CardDisplayVariables.miniAdamsCardDisplayVariables().container,
		}
	}
}
